/**
 * Record Payment Modal
 * Modal for recording payments against invoices
 */

import React, { useState } from 'react';
import { recordPayment } from '../services/invoiceService';
import { formatCurrency } from '../utils/formatters';
import './Modal.css';

const RecordPaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const remainingAmount = invoice.totalAmount - (invoice.paidAmount || 0);
  
  const [formData, setFormData] = useState({
    amount: remainingAmount,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    transactionId: '',
    notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.amount <= 0) {
      setError('Payment amount must be greater than 0');
      return;
    }

    if (formData.amount > remainingAmount) {
      setError(`Payment amount cannot exceed remaining balance of ${formatCurrency(remainingAmount)}`);
      return;
    }

    setLoading(true);

    try {
      await recordPayment(invoice.id, {
        ...formData,
        amount: parseFloat(formData.amount),
      });

      alert('Payment recorded successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Record Payment</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          {/* Invoice Info */}
          <div style={{ 
            background: '#f3f4f6', 
            padding: '1rem', 
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: '500' }}>Invoice:</span>
              <span>{invoice.invoiceNumber}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: '500' }}>Client:</span>
              <span>{invoice.clientName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: '500' }}>Total Amount:</span>
              <span>{formatCurrency(invoice.totalAmount)}</span>
            </div>
            {invoice.paidAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '500' }}>Already Paid:</span>
                <span style={{ color: '#059669' }}>{formatCurrency(invoice.paidAmount)}</span>
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              paddingTop: '0.5rem',
              borderTop: '2px solid #d1d5db',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontWeight: '600' }}>Remaining:</span>
              <span style={{ fontWeight: '600', color: '#dc2626' }}>
                {formatCurrency(remainingAmount)}
              </span>
            </div>
          </div>

          <form id="paymentForm" onSubmit={handleSubmit}>
            {/* Amount */}
            <div className="form-group">
              <label htmlFor="amount">Payment Amount *</label>
              <input
                id="amount"
                type="number"
                className="input"
                min="0.01"
                step="0.01"
                max={remainingAmount}
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                required
              />
              <p className="help-text">
                Maximum: {formatCurrency(remainingAmount)}
              </p>
            </div>

            {/* Payment Date */}
            <div className="form-group">
              <label htmlFor="paymentDate">Payment Date *</label>
              <input
                id="paymentDate"
                type="date"
                className="input"
                value={formData.paymentDate}
                onChange={(e) => handleChange('paymentDate', e.target.value)}
                required
              />
            </div>

            {/* Payment Method */}
            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method *</label>
              <select
                id="paymentMethod"
                className="input"
                value={formData.paymentMethod}
                onChange={(e) => handleChange('paymentMethod', e.target.value)}
                required
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="paypal">PayPal</option>
                <option value="stripe">Stripe</option>
                <option value="gcash">GCash</option>
                <option value="paymongo">PayMongo</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Transaction ID */}
            <div className="form-group">
              <label htmlFor="transactionId">Transaction/Reference ID</label>
              <input
                id="transactionId"
                type="text"
                className="input"
                placeholder="e.g., TXN123456, Check #1234"
                value={formData.transactionId}
                onChange={(e) => handleChange('transactionId', e.target.value)}
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="input"
                rows="3"
                placeholder="Additional notes about this payment..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </div>

          </form>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="paymentForm"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
